import { useRef, useState, ChangeEventHandler, CSSProperties } from "react";
import dayjs from "dayjs";
import { Dialog } from "@mui/material";
import I18n from "common/i18n";
import { btnDelay, usePlaySound, useScale } from "common/utils";
import { FadeTransition, CloseBtn, Select } from "common/elements";
import {
  BEST_SUBJECT_OPTIONS,
  ACCESSORY_OPTIONS,
  HAIRSTYLE_OPTIONS,
  BODY_TYPE_OPTIONS,
  FACE_SHAPE_OPTIONS,
  COLOR_PALETTE,
  generateColorVariations,
  BaseColorKey,
} from "../constants";

export type EditingField =
  | "name"
  | "gender"
  | "birthday"
  | "color"
  | "bestSubjects"
  | "accessory"
  | "hairstyle"
  | "bodyType"
  | "faceShape"
  | "selfIntroduction"
  | null;

type ProfileEditModalProps = {
  editingField: EditingField;
  tempValues: string[];
  tempText: string;
  tempName: string;
  tempGender: string;
  tempSingleValue: string;
  tempYear: number | null;
  tempMonth: number | null;
  tempDay: number | null;
  tempBaseColor: BaseColorKey | null;
  tempVariationColor: string | null;
  onTempSelect: (value: string) => void;
  onBaseColorSelect: (baseKey: BaseColorKey) => void;
  onVariationSelect: (hex: string) => void;
  onTempTextChange: (text: string) => void;
  onTempNameChange: ChangeEventHandler<HTMLInputElement>;
  onTempGenderSelect: (gender: string) => void;
  onTempSingleSelect: (value: string) => void;
  onTempYearChange: (year: number) => void;
  onTempMonthChange: (month: number) => void;
  onTempDayChange: (day: number) => void;
  onClose: () => void;
  onConfirm: () => void;
};

const getEditModalTitle = (editingField: EditingField): string => {
  switch (editingField) {
    case "name":
      return I18n.t("MSG_USER_PROFILE_FIELD_NAME");
    case "gender":
      return I18n.t("MSG_USER_PROFILE_FIELD_GENDER");
    case "birthday":
      return I18n.t("MSG_USER_PROFILE_FIELD_BIRTHDAY");
    case "color":
      return I18n.t("MSG_PROFILE_EDIT_COLOR_TITLE");
    case "bestSubjects":
      return I18n.t("MSG_PROFILE_EDIT_BEST_SUBJECTS_TITLE");
    case "accessory":
      return I18n.t("MSG_PROFILE_EDIT_ACCESSORY_TITLE");
    case "hairstyle":
      return I18n.t("MSG_PROFILE_AI_HAIRSTYLE");
    case "bodyType":
      return I18n.t("MSG_PROFILE_AI_BODY_TYPE");
    case "faceShape":
      return I18n.t("MSG_PROFILE_AI_FACE_SHAPE");
    case "selfIntroduction":
      return I18n.t("MSG_PROFILE_AI_OTHER_INFO");
    default:
      return "";
  }
};

const NameEditContent = ({
  tempName,
  onTempNameChange,
}: {
  tempName: string;
  onTempNameChange: ChangeEventHandler<HTMLInputElement>;
}) => (
  <div className="flex-col-el flex-center w-full">
    <div className="flex-col-el flex-center relative w-[502px]">
      <input
        required
        type="text"
        className="peer input type-text type-title text-center text-textcolor-black"
        placeholder={I18n.t("MSG_USER_PROFILE_FIELD_NAME_PLACEHOLDER")}
        pattern={".{1,10}"}
        autoFocus={false}
        value={tempName}
        onChange={onTempNameChange}
      />
      <label className="type-text type-sm text-left h-5 text-danger invisible peer-invalid:visible">
        {I18n.t("MSG_BLOCKLY_INPUT_LENGTH_LIMIT", { limit: 10 })}
      </label>
    </div>
  </div>
);

const GENDER_TILE_OPTIONS: {
  key: string;
  labelKey: string;
  icon: string;
  bgColor: string;
  textColor: string;
}[] = [
  {
    key: "male",
    labelKey: "MSG_USER_PROFILE_FIELD_GENDER_MALE",
    icon: "man",
    bgColor: "bg-blue-100",
    textColor: "text-blue-100",
  },
  {
    key: "female",
    labelKey: "MSG_USER_PROFILE_FIELD_GENDER_FEMALE",
    icon: "woman",
    bgColor: "bg-red-100",
    textColor: "text-red-100",
  },
  {
    key: "none",
    labelKey: "MSG_USER_PROFILE_FIELD_GENDER_NONE",
    icon: "person",
    bgColor: "bg-gray-80",
    textColor: "text-gray-80",
  },
];

const GenderEditContent = ({
  tempGender,
  onTempGenderSelect,
}: {
  tempGender: string;
  onTempGenderSelect: (gender: string) => void;
}) => {
  const play = usePlaySound();

  const handleSelect = (value: string) => {
    play();
    onTempGenderSelect(value);
  };

  return (
    <div className="flex-row-el flex-center gap-4">
      {GENDER_TILE_OPTIONS.map((option) => {
        const isSelected = tempGender === option.key;
        return (
          <div
            key={option.key}
            onClick={() => handleSelect(option.key)}
            className={`
              flex-col-el items-center justify-center w-[160px] h-[160px] rounded-lg
              cursor-pointer select-none relative active:scale-95
              ${isSelected ? option.bgColor : "bg-gray-10"}
            `}
          >
            <span
              className={`material-symbols-outlined absolute top-2 left-2 type-body ${isSelected ? "text-white" : option.textColor}`}
            >
              {isSelected ? "radio_button_checked" : "radio_button_unchecked"}
            </span>
            <span
              className={`material-symbols-outlined type-hero mb-2 ${isSelected ? "text-white" : option.textColor}`}
            >
              {option.icon}
            </span>
            <p
              className={`type-text type-body text-center ${isSelected ? "text-white" : option.textColor}`}
            >
              {I18n.t(option.labelKey)}
            </p>
          </div>
        );
      })}
    </div>
  );
};

const BirthdayEditContent = ({
  tempYear,
  tempMonth,
  tempDay,
  onTempYearChange,
  onTempMonthChange,
  onTempDayChange,
}: {
  tempYear: number | null;
  tempMonth: number | null;
  tempDay: number | null;
  onTempYearChange: (year: number) => void;
  onTempMonthChange: (month: number) => void;
  onTempDayChange: (day: number) => void;
}) => {
  const play = usePlaySound();
  const [yearOpen, setYearOpen] = useState(false);
  const [monthOpen, setMonthOpen] = useState(false);
  const [dayOpen, setDayOpen] = useState(false);
  const yearRef = useRef<HTMLButtonElement>(null);
  const monthRef = useRef<HTMLButtonElement>(null);
  const dayRef = useRef<HTMLButtonElement>(null);

  const today = dayjs();
  const currentYear = today.year();
  const yearSuffixKey = "MSG_BIRTHDAY_YEAR_SUFFIX";
  const monthSuffixKey = "MSG_BIRTHDAY_MONTH_SUFFIX";
  const daySuffixKey = "MSG_BIRTHDAY_DAY_SUFFIX";

  const yearOptions = Array.from({ length: 100 }, (_, i) => {
    const year = currentYear - i;
    return { value: year, label: `${year}${I18n.t(yearSuffixKey)}` };
  });
  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}${I18n.t(monthSuffixKey)}`,
  }));
  const daysInMonth =
    tempYear && tempMonth ? dayjs(`${tempYear}-${tempMonth}-01`).daysInMonth() : 31;
  const dayOptions = Array.from({ length: daysInMonth }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}${I18n.t(daySuffixKey)}`,
  }));

  return (
    <div className="flex-row-el flex-center gap-4">
      <button
        ref={yearRef}
        onClick={() => {
          play();
          setYearOpen(true);
        }}
        className="type-inherit-font flex items-center justify-between gap-2 py-3 px-4 rounded-lg type-text type-body cursor-pointer select-none active:scale-95 bg-white border-4 border-input-focus text-textcolor-black w-[200px]"
      >
        <span>{tempYear ? `${tempYear}${I18n.t(yearSuffixKey)}` : I18n.t(yearSuffixKey)}</span>
        <span className="material-symbols-outlined type-body">expand_more</span>
      </button>
      <Select
        anchorRef={yearRef}
        open={yearOpen}
        onClose={() => setYearOpen(false)}
        value={tempYear}
        onChange={onTempYearChange}
        options={yearOptions}
      />

      <button
        ref={monthRef}
        onClick={() => {
          play();
          setMonthOpen(true);
        }}
        className="type-inherit-font flex items-center justify-between gap-2 py-3 px-4 rounded-lg type-text type-body cursor-pointer select-none active:scale-95 bg-white border-4 border-input-focus text-textcolor-black w-[150px]"
      >
        <span>{tempMonth ? `${tempMonth}${I18n.t(monthSuffixKey)}` : I18n.t(monthSuffixKey)}</span>
        <span className="material-symbols-outlined type-body">expand_more</span>
      </button>
      <Select
        anchorRef={monthRef}
        open={monthOpen}
        onClose={() => setMonthOpen(false)}
        value={tempMonth}
        onChange={onTempMonthChange}
        options={monthOptions}
      />

      <button
        ref={dayRef}
        onClick={() => {
          play();
          setDayOpen(true);
        }}
        className="type-inherit-font flex items-center justify-between gap-2 py-3 px-4 rounded-lg type-text type-body cursor-pointer select-none active:scale-95 bg-white border-4 border-input-focus text-textcolor-black w-[150px]"
      >
        <span>{tempDay ? `${tempDay}${I18n.t(daySuffixKey)}` : I18n.t(daySuffixKey)}</span>
        <span className="material-symbols-outlined type-body">expand_more</span>
      </button>
      <Select
        anchorRef={dayRef}
        open={dayOpen}
        onClose={() => setDayOpen(false)}
        value={tempDay}
        onChange={onTempDayChange}
        options={dayOptions}
      />
    </div>
  );
};

const ColorEditContent = ({
  tempBaseColor,
  tempVariationColor,
  onBaseColorSelect,
  onVariationSelect,
}: {
  tempBaseColor: BaseColorKey | null;
  tempVariationColor: string | null;
  onBaseColorSelect: (baseKey: BaseColorKey) => void;
  onVariationSelect: (hex: string) => void;
}) => {
  const play = usePlaySound();
  const variations = tempBaseColor ? generateColorVariations(tempBaseColor) : [];

  const handleBaseColorClick = (baseKey: BaseColorKey) => {
    play();
    onBaseColorSelect(baseKey);
  };

  const handleVariationClick = (hex: string) => {
    play();
    onVariationSelect(hex);
  };

  return (
    <div className="flex-col-el items-center gap-6">
      <div className="flex-row-el flex-center gap-4">
        {COLOR_PALETTE.map((option) => {
          const isActive = tempBaseColor === option.key;
          const isWhite = option.key === "white";
          return (
            <div
              key={option.key}
              onClick={() => handleBaseColorClick(option.key)}
              className="flex-col-el flex-center gap-2 cursor-pointer select-none"
            >
              <div
                className={`
                  flex-col-el flex-center
                  w-[80px] h-[80px] rounded-full active:scale-95 bg-[color:var(--swatch)]
                  ${isActive ? "ring-4 ring-green-100" : ""}
                  ${isWhite ? "border-2 border-gray-40" : ""}
                `}
                style={{ "--swatch": option.hex } as CSSProperties}
              >
                {isActive && (
                  <span className="material-symbols-outlined type-title text-white drop-shadow-md">
                    check
                  </span>
                )}
              </div>
              <span
                className={`type-text type-sm ${isActive ? "text-green-100" : "text-textcolor-black/70"}`}
              >
                {I18n.t(option.labelKey)}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex-col-el items-center gap-4">
        <p className="type-text type-sm text-textcolor-black">{I18n.t("MSG_COLOR_SELECT_SHADE")}</p>
        <div className="flex-row-el gap-4">
          {tempBaseColor
            ? variations.map((hex, idx) => {
                const isSelected = tempVariationColor === hex;
                return (
                  <div
                    key={idx}
                    onClick={() => handleVariationClick(hex)}
                    className={`
                      flex-col-el flex-center w-[60px] h-[60px] rounded-full bg-[color:var(--swatch)]
                      cursor-pointer select-none active:scale-95
                      ${isSelected ? "ring-4 ring-green-100" : ""}
                    `}
                    style={{ "--swatch": hex } as CSSProperties}
                  >
                    {isSelected && (
                      <span className="material-symbols-outlined type-body text-white drop-shadow-md">
                        check
                      </span>
                    )}
                  </div>
                );
              })
            : Array.from({ length: 5 }).map((_, idx) => (
                <div
                  key={idx}
                  className="flex-col-el flex-center w-[60px] h-[60px] rounded-full border-2 border-dashed border-gray-40"
                />
              ))}
        </div>
      </div>
    </div>
  );
};

const SubjectsEditContent = ({
  tempValues,
  onTempSelect,
}: {
  tempValues: string[];
  onTempSelect: (value: string) => void;
}) => {
  const play = usePlaySound();

  const handleSelect = (value: string) => {
    play();
    onTempSelect(value);
  };

  return (
    <div className="grid grid-cols-4 gap-3 justify-items-center">
      {BEST_SUBJECT_OPTIONS.map((option) => {
        const isActive = tempValues.includes(option.key);
        return (
          <div
            key={option.key}
            onClick={() => handleSelect(option.key)}
            className={`
              flex-col-el flex-center relative
              w-[120px] h-[120px]
              rounded-lg
              cursor-pointer select-none
              active:scale-95
              ${isActive ? "bg-green-alt border-2 border-green-alt" : "bg-surface border-2 border-green-alt"}
            `}
          >
            {isActive && (
              <span className="absolute top-1 right-1 material-symbols-outlined type-sm text-green-100">
                check_circle
              </span>
            )}
            {option.icon && (
              <span
                className={`material-symbols-outlined type-title mb-1 ${isActive ? "text-green-100" : "text-green-80"}`}
              >
                {option.icon}
              </span>
            )}
            <span
              className={`type-text type-body text-center ${isActive ? "text-textcolor-black" : "text-textcolor-black/70"}`}
            >
              {I18n.t(option.labelKey)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const AccessoryEditContent = ({
  tempValues,
  onTempSelect,
}: {
  tempValues: string[];
  onTempSelect: (value: string) => void;
}) => {
  const play = usePlaySound();

  const handleSelect = (value: string) => {
    play();
    onTempSelect(value);
  };

  return (
    <div className="grid grid-cols-3 gap-4 justify-items-center">
      {ACCESSORY_OPTIONS.map((option) => {
        const isActive = tempValues.includes(option.key);
        return (
          <div
            key={option.key}
            onClick={() => handleSelect(option.key)}
            className={`
              flex-col-el flex-center relative
              w-[140px] h-[140px]
              rounded-lg
              cursor-pointer select-none
              active:scale-95
              ${isActive ? "bg-green-alt border-2 border-green-alt" : "bg-surface border-2 border-green-alt"}
            `}
          >
            {isActive && (
              <span className="absolute top-1 right-1 material-symbols-outlined type-sm text-green-100">
                check_circle
              </span>
            )}
            {option.icon && (
              <span
                className={`material-symbols-outlined type-title mb-1 ${isActive ? "text-green-100" : "text-green-80"}`}
              >
                {option.icon}
              </span>
            )}
            <span
              className={`type-text type-body text-center ${isActive ? "text-textcolor-black" : "text-textcolor-black/70"}`}
            >
              {I18n.t(option.labelKey)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const SingleSelectEditContent = ({
  options,
  tempSingleValue,
  onTempSingleSelect,
}: {
  options: Array<{ key: string; icon?: string; labelKey: string }>;
  tempSingleValue: string;
  onTempSingleSelect: (value: string) => void;
}) => {
  const play = usePlaySound();

  const handleSelect = (value: string) => {
    play();
    onTempSingleSelect(value);
  };

  return (
    <div className="grid grid-cols-5 gap-3 justify-items-center">
      {options.map((option) => {
        const isActive = tempSingleValue === option.key;
        return (
          <div
            key={option.key}
            onClick={() => handleSelect(option.key)}
            className={`
              flex-col-el flex-center relative
              w-[110px] h-[110px]
              rounded-lg
              cursor-pointer select-none
              active:scale-95
              ${isActive ? "bg-green-alt border-2 border-green-alt" : "bg-surface border-2 border-green-alt"}
            `}
          >
            {isActive && (
              <span className="absolute top-1 right-1 material-symbols-outlined type-sm text-green-100">
                check_circle
              </span>
            )}
            {option.icon && (
              <span
                className={`material-symbols-outlined type-md mb-1 ${isActive ? "text-green-100" : "text-green-80"}`}
              >
                {option.icon}
              </span>
            )}
            <span
              className={`type-text type-sm text-center px-1 ${isActive ? "text-textcolor-black" : "text-textcolor-black/70"}`}
            >
              {I18n.t(option.labelKey)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const SelfIntroductionEditContent = ({
  tempText,
  onTempTextChange,
}: {
  tempText: string;
  onTempTextChange: (text: string) => void;
}) => (
  <div className="flex-col-el items-center w-full">
    <div className="flex-col-el w-full max-w-[520px]">
      <textarea
        className="input-modal type-text type-sm text-textcolor-black resize-none"
        rows={5}
        placeholder={I18n.t("MSG_PROFILE_AI_OTHER_INFO_PLACEHOLDER")}
        value={tempText}
        onChange={(e) => onTempTextChange(e.target.value)}
        maxLength={200}
      />
      <p className="type-text type-sm text-right text-gray2-80 mt-2">{tempText.length}/200</p>
    </div>
  </div>
);

export const ProfileEditModal = ({
  editingField,
  tempValues,
  tempText,
  tempName,
  tempGender,
  tempSingleValue,
  tempYear,
  tempMonth,
  tempDay,
  tempBaseColor,
  tempVariationColor,
  onTempSelect,
  onBaseColorSelect,
  onVariationSelect,
  onTempTextChange,
  onTempNameChange,
  onTempGenderSelect,
  onTempSingleSelect,
  onTempYearChange,
  onTempMonthChange,
  onTempDayChange,
  onClose,
  onConfirm,
}: ProfileEditModalProps) => {
  const play = usePlaySound();
  const { scale } = useScale();

  const handleClose = () => {
    play();
    btnDelay(() => onClose());
  };

  const handleConfirm = () => {
    play();
    btnDelay(() => onConfirm());
  };

  const getModalHeight = () => {
    switch (editingField) {
      case "bestSubjects":
        return 780;
      case "accessory":
        return 580;
      case "color":
        return 440;
      case "name":
        return 360;
      case "gender":
        return 420;
      case "birthday":
        return 360;
      case "hairstyle":
      case "bodyType":
      case "faceShape":
        return 380;
      default:
        return 450;
    }
  };
  const modalHeight = getModalHeight();

  const renderContent = () => {
    switch (editingField) {
      case "name":
        return <NameEditContent tempName={tempName} onTempNameChange={onTempNameChange} />;
      case "gender":
        return (
          <GenderEditContent tempGender={tempGender} onTempGenderSelect={onTempGenderSelect} />
        );
      case "birthday":
        return (
          <BirthdayEditContent
            tempYear={tempYear}
            tempMonth={tempMonth}
            tempDay={tempDay}
            onTempYearChange={onTempYearChange}
            onTempMonthChange={onTempMonthChange}
            onTempDayChange={onTempDayChange}
          />
        );
      case "color":
        return (
          <ColorEditContent
            tempBaseColor={tempBaseColor}
            tempVariationColor={tempVariationColor}
            onBaseColorSelect={onBaseColorSelect}
            onVariationSelect={onVariationSelect}
          />
        );
      case "bestSubjects":
        return <SubjectsEditContent tempValues={tempValues} onTempSelect={onTempSelect} />;
      case "accessory":
        return <AccessoryEditContent tempValues={tempValues} onTempSelect={onTempSelect} />;
      case "hairstyle":
        return (
          <SingleSelectEditContent
            options={HAIRSTYLE_OPTIONS}
            tempSingleValue={tempSingleValue}
            onTempSingleSelect={onTempSingleSelect}
          />
        );
      case "bodyType":
        return (
          <SingleSelectEditContent
            options={BODY_TYPE_OPTIONS}
            tempSingleValue={tempSingleValue}
            onTempSingleSelect={onTempSingleSelect}
          />
        );
      case "faceShape":
        return (
          <SingleSelectEditContent
            options={FACE_SHAPE_OPTIONS}
            tempSingleValue={tempSingleValue}
            onTempSingleSelect={onTempSingleSelect}
          />
        );
      case "selfIntroduction":
        return (
          <SelfIntroductionEditContent tempText={tempText} onTempTextChange={onTempTextChange} />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={editingField !== null}
      fullScreen
      onClose={handleClose}
      maxWidth={false}
      sx={{ zIndex: 999999 }}
      TransitionComponent={FadeTransition}
      componentsProps={{
        backdrop: { style: { backgroundColor: "rgba(0, 0, 0, 0.5)" } },
      }}
      PaperProps={{
        style: { margin: 0, overflow: "visible", backgroundColor: "transparent" },
        elevation: 0,
      }}
    >
      <div className="flex-col-el flex-center w-full h-full">
        <div
          className="flex-col-el flex-center relative"
          style={{
            width: 660 * scale,
            height: modalHeight * scale,
          }}
        >
          <div
            className="flex-col-el shrink-0 border-solid border-white border-[8px] rounded-lg bg-beige-60 relative drop-shadow-[0px_6px_6px_rgba(0,0,0,0.3)]"
            style={{
              width: 660,
              height: modalHeight,
              transform: `scale(${scale})`,
            }}
          >
            <CloseBtn close={handleClose} />

            <div className="flex-col-el flex-center py-4">
              <p className="type-text type-body text-textcolor-black">
                {getEditModalTitle(editingField)}
              </p>
              {(editingField === "bestSubjects" || editingField === "accessory") && (
                <p className="type-text type-sm text-textcolor-black/60 mt-1">
                  ({I18n.t("MSG_PROFILE_MULTI_SELECT")})
                </p>
              )}
            </div>

            <div className="flex-col-el flex-center flex-1 px-4">{renderContent()}</div>

            <div className="flex-col-el flex-center py-4 alpha">
              <button
                className="btn btn-primary"
                onClick={handleConfirm}
                disabled={
                  (editingField === "color" && !tempVariationColor) ||
                  (editingField === "bestSubjects" && tempValues.length === 0) ||
                  (editingField === "accessory" && tempValues.length === 0) ||
                  (editingField === "name" && (tempName.length === 0 || tempName.length > 10)) ||
                  ((editingField === "hairstyle" ||
                    editingField === "bodyType" ||
                    editingField === "faceShape") &&
                    !tempSingleValue)
                }
              >
                <div className="flex-row-center gap-1 mb-1">
                  <span className="material-symbols-outlined !text-4xl text-white">check</span>
                  <p className="text-body text-white text-font-size-body1">
                    {I18n.t("MSG_CONFIRM_BTN")}
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
